/**
 * codeBlocks.js
 * Code block enhancement utilities for syntax highlighting and copy functionality
 */

/**
 * Idempotent helper: normalizes language classes and adds copy buttons/badges to code blocks
 * @param {HTMLElement} container - Container element to search for code blocks
 */
export function enhanceCodeBlocks(container) {
  const codeBlocks = container.querySelectorAll('pre');
  codeBlocks.forEach((pre) => {
    // Skip if already has copy button (idempotent)
    if (pre.querySelector('.code-copy-btn')) return;

    // Normalize language class: move from code to pre if needed
    const codeElement = pre.querySelector('code');
    if (codeElement) {
      const langClass = Array.from(codeElement.classList).find(c => c.startsWith('language-'));
      if (langClass && !pre.classList.contains(langClass)) {
        pre.classList.add(langClass);
      }
    }

    // Language Badge - only if missing
    const langClass = Array.from(pre.classList).find(c => c.startsWith('language-'));
    if (langClass && !pre.querySelector('.code-lang-badge')) {
      const lang = langClass.replace('language-', '');
      const badge = document.createElement('div');
      badge.className = 'code-lang-badge';
      badge.textContent = lang;
      pre.appendChild(badge);
    }

    // Copy Button - only if missing
    if (!pre.querySelector('.code-copy-btn')) {
      const copyBtn = document.createElement("button");
      copyBtn.className = "code-copy-btn";
      copyBtn.title = "Copy code";
      copyBtn.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>`;

      // Click handler with stopPropagation
      copyBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        const codeEl = pre.querySelector('code') || pre;
        const codeContent = codeEl.innerText || codeEl.textContent;

        // Try modern clipboard API first, fallback to execCommand for HTTP
        const copyToClipboard = async (text) => {
          try {
            if (navigator.clipboard && window.isSecureContext) {
              await navigator.clipboard.writeText(text);
            } else {
              // Fallback for HTTP contexts
              const textArea = document.createElement('textarea');
              textArea.value = text;
              textArea.style.position = 'fixed';
              textArea.style.left = '-9999px';
              document.body.appendChild(textArea);
              textArea.select();
              document.execCommand('copy');
              document.body.removeChild(textArea);
            }
            return true;
          } catch (err) {
            console.error('Copy failed:', err);
            return false;
          }
        };

        copyToClipboard(codeContent).then((success) => {
          if (success) {
            copyBtn.classList.add("copied");
            setTimeout(() => {
              copyBtn.classList.remove("copied");
            }, 2000);
          }
        });
      });

      pre.appendChild(copyBtn);
    }
  });
}
